import { Controller } from '@hotwired/stimulus'

export default class ChartRebrandingController extends Controller {
  static values = {
    labels: Array,
    colors: Array,
    data: Array,
  }

  connect() {
    this.chart = this.createChart();
  }

  disconnect() {
    this.chart?.destroy();
  }

  createChart() {
    const ctx = this.element;

    return new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: this.labelsValue,
        datasets: [{
          data: this.dataValue,
          backgroundColor: this.colorsValue,
        }],
      },
      options: {
        animation: false,
        borderWidth: 0,
        plugins: {
          htmlLegend: {
            containerID: 'legend-container',
          },
          legend: {
            display: false,
          },
        },
        responsive: true,
        cutout: '80%',
      },
      plugins: [this.htmlLegendPlugin()],
    });
  }

  htmlLegendPlugin() {
    let chartController = this;
    return {
      id: 'htmlLegend',
      afterUpdate(chart, args, options) {
        const ul = chartController.getOrCreateLegendList(chart, options.containerID);

        // Remove old legend items
        while (ul.firstChild) {
          ul.firstChild.remove();
        }

        // Reuse the built-in legendItems generator
        const items = chart.options.plugins.legend.labels.generateLabels(chart);
        const dataset = chart.data.datasets[0].data;
        const total = dataset.reduce((a, b) => parseInt(a) + parseInt(b), 0);

        items.forEach(item => {
          const li = document.createElement('li');
          li.style.marginBottom = '0.25rem'

          const btn = document.createElement('button');
          btn.classList.add(
            'unset-btn-appearance',
            'd-flex',
            'align-items-baseline',
            'flex-row',
          )

          btn.onclick = () => {
            const {type} = chart.config;
            if (type === 'pie' || type === 'doughnut') {
              // Pie and doughnut charts only have a single dataset and visibility is per item
              chart.toggleDataVisibility(item.index);
            } else {
              chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
            }
            chart.update();
          };

          // Color box
          const boxSpan = document.createElement('span');
          boxSpan.classList.add(
            'd-inline-block',
            'flex-shrink-0',
            'p-1',
            'rounded-circle',
            'me-2',
          );
          boxSpan.style.background = item.fillStyle;

          // Text
          const labelContainer = document.createElement('span');
          labelContainer.classList.add(
            'm-0',
            'p-0',
            'fw-semibold',
            'text-sm'
          );
          labelContainer.style.textDecoration = item.hidden ? 'line-through' : '';

          const text = document.createTextNode(item.text);
          labelContainer.appendChild(text);


          const rightSection = document.createElement('span');
          rightSection.classList.add(
            'd-flex',
            'flex-column',
            'align-items-start',
            'gap-1',
          );
          const figuresContainer = document.createElement('span');
          figuresContainer.classList.add(
            'd-flex',
            'align-items-center',
            'gap-2',
            'text-gray',
            'text-xs',
            'fw-medium',
          );

          const separator = document.createElement('span');
          separator.classList.add('border-end', 'h-2');

          const amount = dataset[item.index];
          const amountContainer = document.createElement('span');

          amountContainer.innerText = new Intl.NumberFormat(
            'fr-FR',
            { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }
          ).format(amount);

          const percentage = ((amount / total) * 100).toFixed(2);
          const percentContainer = document.createElement('span');
          percentContainer.innerText = `${percentage} %`;

          figuresContainer.appendChild(amountContainer);
          figuresContainer.appendChild(separator);
          figuresContainer.appendChild(percentContainer);

          rightSection.appendChild(labelContainer);
          rightSection.appendChild(figuresContainer);
          btn.appendChild(boxSpan);
          btn.appendChild(rightSection);
          li.appendChild(btn);
          ul.appendChild(li);
        });
      }
    }
  }

  getOrCreateLegendList(chart, id) {
    const legendContainer = document.getElementById(id);
    let listContainer = legendContainer.querySelector('ul');

    if (!listContainer) {
      listContainer = document.createElement('ul');
      listContainer.classList.add(
        'list-unstyled',
        'm-0',
        'p-0',
      )

      legendContainer.appendChild(listContainer);
    }

    return listContainer;
  }
}
